import React, { useState, useCallback, Fragment, useEffect } from 'react';
import { X, User, Phone, MapPin, ArrowRight, Sun, Sparkles, PartyPopper, Ticket, CreditCard, ExternalLink, Copy, Check, ChevronDown } from 'lucide-react';

// --- COMPONENTES DE UI MEJORADOS (TEMA CLARO) ---

// Spinner con color adaptable
const Spinner = () => (
  <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// Input con diseño limpio y moderno
const InputField = ({ icon, ...props }) => (
  <div className="relative group">
    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-teal-500 transition-colors duration-300">
      {icon}
    </div>
    <input
      {...props}
      className="w-full pl-12 pr-4 py-3 bg-slate-100 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300"
    />
  </div>
);

// Componente para un detalle de pago individual con botón para copiar (compacto)
const CopyablePaymentDetail = ({ icon, label, value, onCopy, isCopied }) => (
  <div className="flex items-center justify-between text-xs">
    <div className="flex items-center gap-3 text-slate-600">
      {React.cloneElement(icon, { size: 14, className: "text-slate-400" })}
      <span className="font-medium text-xs">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="font-mono text-slate-800 text-xs tracking-tight break-words">{value}</span>
      <button
        onClick={onCopy}
        className="p-1 rounded-md hover:bg-slate-200 active:bg-slate-300 transition-colors"
        aria-label={`Copiar ${label}`}
      >
        {isCopied
          ? <Check size={14} className="text-emerald-500" />
          : <Copy size={14} className="text-slate-500" />
        }
      </button>
    </div>
  </div>
);


// Barra de progreso visual para los pasos
const ProgressBar = ({ step }) => (
  <div className="flex items-center w-full px-6 pt-6">
    <div className="flex flex-col items-center relative">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${step >= 1 ? 'bg-teal-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
        <User size={20} />
      </div>
      <p className={`mt-2 text-xs font-semibold transition-all duration-500 ${step >= 1 ? 'text-teal-600' : 'text-slate-400'}`}>Registro</p>
    </div>
    <div className="flex-grow h-1 bg-slate-200 relative mx-4">
        <div className="absolute top-0 left-0 h-1 bg-teal-500 transition-all duration-500" style={{ width: step === 2 ? '100%' : '0%' }}></div>
    </div>
    <div className="flex flex-col items-center relative">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${step >= 2 ? 'bg-teal-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
        <CreditCard size={20} />
      </div>
      <p className={`mt-2 text-xs font-semibold transition-all duration-500 ${step >= 2 ? 'text-teal-600' : 'text-slate-400'}`}>Pago</p>
    </div>
  </div>
);

// --- COMPONENTE PRINCIPAL ---

const Registro = ({ selectedCount, selectedTicketNumbers, onClose, expirationHours, onConfirm }) => {
  const [formData, setFormData] = useState({ name: '', phone: '', state: '', municipio: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [isClosing, setIsClosing] = useState(false);

  // --- Estados y lógica para el PASO 2 (NUEVO) ---
  const [openAccount, setOpenAccount] = useState('ARTURO MALDONADO LUNA');
  const [copiedValue, setCopiedValue] = useState(null);

  const handleCopy = (value) => {
    navigator.clipboard.writeText(value.replace(/\s/g, '')); // Copia sin espacios
    setCopiedValue(value);
    setTimeout(() => setCopiedValue(null), 2000); // Resetea el estado después de 2s
  };
  // Logos de los bancos (tomados de la configuración en App.js)
  const BANORTE_LOGO = 'https://github.com/vordensports/vordensports/blob/main/Logo_de_Banorte.svg.png?raw=true';
  const SANTANDER_LOGO = 'https://github.com/vordensports/vordensports/blob/main/Santander.png?raw=true';

  // Información real de cuentas (BBVA, Banorte, Santander)
  const paymentAccounts = [
    {
      name: 'ARTURO MALDONADO LUNA (BBVA)',
      details: [
        { icon: <CreditCard />, label: 'CUENTA:', value: '0488051046' },
        { icon: <Ticket />, label: 'CLABE:', value: '012244004880510463' },
      ]
    },
    {
      name: 'MCMS Proyectos y soluciones Sa de Cv (BBVA)',
      details: [
        { icon: <CreditCard />, label: 'CUENTA:', value: '0125074215' },
        { icon: <Ticket />, label: 'CLABE:', value: '012244001250742152' },
      ]
    },
    {
      name: 'ARTURO MALDONADO LUNA (Banorte)',
      details: [
        { icon: <CreditCard />, label: 'CUENTA:', value: '1337594064' },
        { icon: <Ticket />, label: 'CLABE:', value: '072244013375940647' },
      ]
    },
   
  ];
  // --- Fin de la lógica para el PASO 2 ---

  // Calcular monto a liquidar: $20 por boleto
  const amountToPay = (Number(selectedCount) || 0) * 20;

  // Efecto para bloquear el scroll del body cuando el modal está abierto
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  
  // Nota: eliminada la simulación local; el guardado real lo realiza la prop onConfirm pasada desde App.js

  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Phone input: accept only digits, max length 10 (mobile-first behavior)
  const handlePhoneChange = useCallback((e) => {
    const raw = e.target.value || '';
    // Keep digits only and limit to 10
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, phone: digits }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.state) {
        console.error("Por favor completa los campos requeridos.");
        return;
    }
    setIsLoading(true);
    try {
      // Llamamos a la función pasada desde App.js que guarda/actualiza la reservación en Firestore
      if (typeof onConfirm === 'function') {
        await onConfirm({ ...formData });
      } else {
        console.warn('onConfirm no está disponible en Registro, simulando guardado.');
      }
      setStep(2);
    } catch (error) {
      console.error('❌ Error al guardar en Firebase (Registro -> onConfirm):', error);
      // Aquí podrías mostrar una notificación al usuario
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 400); 
  };

  // Construir enlace dinámico de WhatsApp con mensaje prellenado
  const waNumber = '5214779231372'; // México +52
  const waMessage = `Hola, mi nombre es ${formData.name || ''}\nLos datos a continuacion son los mismos registrados en Rifaselchaman.com.mx: \nMi número de teléfono es: ${formData.phone || ''}\n\nEstado: ${formData.state || ''}\n\nLos boletos apartados son: ${(Array.isArray(selectedTicketNumbers) ? selectedTicketNumbers.join(', ') : selectedTicketNumbers) || ''}\n\nMonto a liquidar: $${amountToPay.toLocaleString()}\n\nEstoy participando en esta *Primera Edicion*🍀.\n\nPuedes volver a encontrar las cuentas de deposito o transferencia en rifaselchaman.com.mx en la parte superior iquierda.\n\nAl enviar tu comprobante de pago, ya estas participando en la edición y tus boletos estarán confirmados, *te deseamos muchisima suerte.*🍀\n\nRifas con Causa El Chamán. Envia este mensaje 👉 `;
  const whatsappHref = `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;

  return (
    <Fragment>
      <style>{`
        @keyframes slide-in-up {
          from { opacity: 0; transform: translateY(30px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slide-out-down {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to { opacity: 0; transform: translateY(30px) scale(0.98); }
        }
        @keyframes fade-in-item {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-in-up { animation: slide-in-up 0.4s ease-out forwards; }
        .animate-slide-out-down { animation: slide-out-down 0.4s ease-in forwards; }
        .form-item { animation: fade-in-item 0.5s ease-out forwards; opacity: 0; }
      `}</style>
      
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-center items-center p-4">
        <div
          className={`bg-white rounded-2xl shadow-2xl w-full max-w-md relative text-slate-800 border border-slate-200 flex flex-col max-h-[95vh] ${isClosing ? 'animate-slide-out-down' : 'animate-slide-in-up'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <ProgressBar step={step} />

          {/* Close / Back button moved to top-left and made more visible */}
          <button
            onClick={handleClose}
            aria-label="Regresar"
            className="absolute top-4 left-4 flex items-center gap-2 bg-white border border-slate-200 px-3 py-2 rounded-full text-slate-800 hover:bg-slate-50 shadow-sm z-20"
          >
            <X size={18} />
            <span className="text-sm font-medium">Regresar</span>
          </button>
          
          <div className="relative overflow-hidden flex-grow min-h-0 flex flex-col">
            <div className="transition-transform duration-500 ease-in-out flex h-full" style={{ transform: `translateX(-${(step - 1) * 100}%)` }}>
              
              {/* --- PASO 1: FORMULARIO --- */}
              <div className="min-w-full p-6 space-y-6 overflow-y-auto">
                

 <div className="shrink-0 space-y-4">
                  <div className="text-center p-3 bg-blue-100 text-blue-800 rounded-xl text-sm border border-green-200">
                    <p className="font-semibold">¡Aparta tus boletos!</p>
                    <p>Ingresa tus datos, solo seran recabados para contactar al ganador.  </p>
                  </div>
                  </div>



                <div className="form-item" style={{ animationDelay: '0.2s' }}>
                  <p className="text-center text-slate-600 mb-2">
                    Has seleccionado <span className="font-bold text-teal-600 text-lg">{selectedCount}</span> boleto(s):
                  </p>
                  <div className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-3 max-h-28 overflow-y-auto flex flex-wrap justify-center gap-2">
                    {selectedTicketNumbers.map(num => (
                      <span key={num} className="bg-teal-100 text-teal-700 font-mono text-sm font-semibold py-1 px-3 rounded-full">
                        {num}
                      </span>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="form-item" style={{ animationDelay: '0.3s' }}><InputField icon={<User size={20} />} type="text" name="name" placeholder="Nombre completo" value={formData.name} onChange={handleFormChange} required /></div>
                  <div className="form-item" style={{ animationDelay: '0.4s' }}>
                    <InputField
                      icon={<Phone size={20} />}
                      type="tel"
                      name="phone"
                      placeholder="Teléfono WhatsApp"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      required
                    />
                  </div>
                  <div className="form-item" style={{ animationDelay: '0.5s' }}><InputField icon={<MapPin size={20} />} type="text" name="state" placeholder="Estado" value={formData.state} onChange={handleFormChange} required /></div>
                  
                  
                  <div className="text-center text-xs text-slate-500 pt-2 flex items-center justify-center gap-2 form-item" style={{ animationDelay: '0.7s' }}>
                    <Sun size={14} className="text-amber-500" />
                    <span>Tienes <span className="font-bold text-amber-600">{expirationHours} horas</span> para completar tu pago.</span>
                  </div>
                  
                  <div className="pt-2 form-item" style={{ animationDelay: '0.8s' }}>
                    {/** Disable submit until required fields are filled and phone has exactly 10 digits */}
                    {(() => {
                      const isPhoneValid = /^\d{10}$/.test(formData.phone || '');
                      const canSubmit = !isLoading && isPhoneValid && formData.name && formData.state;
                      return (
                        <button
                          type="submit"
                          disabled={!canSubmit}
                          className={`w-full ${canSubmit ? 'bg-teal-500 hover:bg-teal-600' : 'bg-slate-300 cursor-not-allowed'} text-white font-bold py-4 rounded-xl text-lg transition-all flex justify-center items-center h-[60px] transform active:scale-[0.98] duration-300 shadow-lg ${canSubmit ? 'shadow-teal-500/30' : ''}`}
                        >
                          {isLoading ? <Spinner /> : <span className="flex items-center gap-2">Confirmar y Apartar <ArrowRight size={20} /></span>}
                        </button>
                      );
                    })()}
                  </div>
                </form>
              </div>

              {/* --- PASO 2: PAGO (DISEÑO RENOVADO Y RESPONSIVO) --- */}
              <div className="min-w-full flex flex-col h-full p-4">
                {/* --- CABECERA --- */}
                  
                 <div className="shrink-0 space-y-4">
                  <div className="text-center p-3 bg-blue-100 text-blue-800 rounded-xl text-sm border border-green-200">
                    <p className="font-semibold">¡Aparta tus boletos!</p>
                    <p>No olvides enviar tu comprobante, con tu nombre completo como referencia, para validar tus boletos. </p>
                    <div className="px-2">
                    <p className="text-sm text-slate-600">Monto a liquidar: <span className="font-bold text-lg text-slate-800">${amountToPay.toLocaleString()}</span></p>
                  </div>
                  </div>
                  </div>

                {/* --- ÁREA DE CONTENIDO CON SCROLL --- */}
                <div className="flex-grow my-6 overflow-y-auto pr-2 space-y-3">
                  <div className="flex items-center gap-3 px-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/BBVA_2019.svg/2560px-BBVA_2019.svg.png" alt="BBVA Logo" className="h-5 md:h-6 object-contain" />
                      <img src={BANORTE_LOGO} alt="Banorte Logo" className="h-5 md:h-6 object-contain" />
                      <img src={SANTANDER_LOGO} alt="Santander Logo" className="h-5 md:h-6 object-contain" />
                    </div>
                    <h3 className="font-bold text-slate-800 text-sm ml-2">Selecciona una cuenta y realiza transferencia o depósito.</h3>
                  </div>

                  {/* Monto a liquidar basado en boletos apartados ($20 por boleto) */}
                  
                  

                 {/* Acordeón de Cuentas - Versión Compacta Mobile */}
<div className="space-y-2">
  {paymentAccounts.map((account) => {
    const isOpen = openAccount === account.name;
    return (
      <div key={account.name} className="border border-slate-200 rounded-xl transition-all duration-300">
        <button
          onClick={() => setOpenAccount(isOpen ? null : account.name)}
          className="w-full flex justify-between items-center p-2 text-left font-bold text-slate-800 text-xs"
        >
          {/* Nombre con ajuste de texto y tamaño xs */}
          <span className="pr-2 break-words whitespace-normal leading-tight">{account.name}</span>
          <ChevronDown
            size={16}
            className={`text-slate-500 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {isOpen && (
          <div className="px-3 pb-3 border-t border-slate-200 space-y-2">
            {account.details.map((detail) => (
              <CopyablePaymentDetail
                key={detail.label}
                icon={detail.icon}
                label={detail.label}
                value={detail.value}
                isCopied={copiedValue === detail.value}
                onCopy={() => handleCopy(detail.value)}
              />
            ))}
          </div>
        )}
      </div>
    );
  })}
</div>
</div>
                {/* --- PIE DE PÁGINA / ÁREA DE ACCIÓN --- */}
                

                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-green-500 text-white font-bold py-3.5 px-4 rounded-xl text-base hover:bg-green-600 transition-all flex justify-center items-center gap-2.5 transform hover:scale-[1.03] active:scale-[0.98] duration-300 shadow-lg shadow-green-500/40"
                  >
                     Enviar Comprobante Whatsapp <ExternalLink size={20} />
                  </a>
                  <div className="text-center text-teal-600 font-bold flex items-center justify-center gap-2 text-sm">
                    <Sparkles size={16} />
                    <span>¡Gracias y mucha suerte!</span>
                  </div>
                
              </div>

            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Registro;
